import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Home } from 'lucide-react';
import { Badge } from '@/components/common/Badge';
import { Card } from '@/components/common/Card';
import { ErrorState, LoadingState } from '@/components/common/States';
import { ROUTES } from '@/app/router/routes';
import { portalApi } from '@/services/api/endpoints/portal';
import { queryKeys } from '@/services/api/queryKeys';
import { formatDate } from '@/utils/format';
import { LiveClock } from '@/components/os/LiveClock';
import { useAuth } from '@/features/auth/useAuth';
import { PortalShell, type PortalContext } from './PortalShell';
import {
  SectionTitle,
  StudentSummaryCard,
  entriesForDay,
  isNotFound,
  percentText,
  todayDay,
} from './shared';

/** A student's home: their own card, today's lessons, and their latest published result. */
export function OverviewPage() {
  const { user } = useAuth();
  return (
    <PortalShell
      title="My school"
      icon={<Home className="h-5 w-5" aria-hidden="true" />}
      action={<LiveClock timeZone={user?.schoolTimezone} />}
    >
      {(context) => <Overview context={context} />}
    </PortalShell>
  );
}

function Overview({ context }: { context: PortalContext }) {
  const { subject, subjectKey, student, withChild } = context;

  const timetable = useQuery({
    queryKey: queryKeys.portal.timetable(subjectKey),
    queryFn: () => portalApi.timetable(subject),
    retry: false,
  });

  const results = useQuery({
    queryKey: queryKeys.portal.results(subjectKey),
    queryFn: () => portalApi.results(subject),
  });

  const today = todayDay();
  const lessons = timetable.data === undefined ? [] : entriesForDay(timetable.data.entries, today);
  // Newest first, as the server returns them.
  const latest = results.data?.at(0);

  return (
    <div className="space-y-4">
      {student !== undefined && (
        <StudentSummaryCard student={student} subject={subject} linkFor={withChild} />
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <SectionTitle
            action={
              <Link to={withChild(ROUTES.myTimetable)} className="text-xs font-semibold text-muted hover:text-ink">
                Whole week
              </Link>
            }
          >
            Today’s lessons
          </SectionTitle>
          {timetable.isPending ? (
            <LoadingState label="Loading your timetable" />
          ) : timetable.isError ? (
            isNotFound(timetable.error) ? (
              <p className="px-4 py-6 text-sm text-muted">No timetable has been published.</p>
            ) : (
              <ErrorState error={timetable.error} onRetry={() => void timetable.refetch()} />
            )
          ) : lessons.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted">No lessons today.</p>
          ) : (
            <ul className="divide-y divide-line">
              {lessons.map((entry) => (
                <li key={entry.id} className="grid grid-cols-[88px_minmax(0,1fr)] gap-3 px-4 py-2.5">
                  <span className="text-xs font-semibold text-muted">{entry.periodName ?? 'Period'}</span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-ink">
                      {entry.subjectName ?? '—'}
                    </span>
                    <span className="block truncate text-xs text-muted">
                      {[entry.teacherName, entry.room].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <SectionTitle
            action={
              <Link to={withChild(ROUTES.myResults)} className="text-xs font-semibold text-muted hover:text-ink">
                All results
              </Link>
            }
          >
            Latest result
          </SectionTitle>
          {results.isPending ? (
            <LoadingState label="Loading results" />
          ) : results.isError ? (
            <ErrorState error={results.error} onRetry={() => void results.refetch()} />
          ) : latest === undefined ? (
            <p className="px-4 py-6 text-sm text-muted">No results have been published yet.</p>
          ) : (
            <Link
              to={withChild(ROUTES.myResult(latest.examinationId))}
              className="block px-4 py-4 transition-colors hover:bg-surface-2"
            >
              <p className="text-sm font-bold text-ink">{latest.examinationName}</p>
              <p className="mt-0.5 text-xs text-muted">
                {[latest.termName, latest.academicYearName].filter(Boolean).join(' · ')}
                {latest.publishedAt !== undefined && ` · published ${formatDate(latest.publishedAt)}`}
              </p>
              <div className="mt-3 flex flex-wrap items-baseline gap-3">
                <span className="text-[26px] font-extrabold leading-none tabular-nums text-ink">
                  {percentText(latest.percentage)}
                </span>
                {latest.grade !== undefined && (
                  <span className="text-sm font-semibold text-ink">Grade {latest.grade}</span>
                )}
                <Badge tone={latest.passing ? 'success' : 'danger'}>
                  {latest.passing ? 'Pass' : 'Fail'}
                </Badge>
              </div>
              <p className="mt-3 text-xs font-semibold text-accent">Open the report card</p>
            </Link>
          )}
        </Card>
      </div>
    </div>
  );
}
